import { forwardRef, type ReactNode, type HTMLAttributes } from 'react';
import { useTheme } from '@vitness/theme';

export type BadgeVariant = 'accent' | 'info' | 'success' | 'warning' | 'danger' | 'muted';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
}

const SEMANTIC_COLORS: Record<string, { color: string; bg: string }> = {
  info: { color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  success: { color: '#22C55E', bg: 'rgba(34,197,94,0.08)' },
  warning: { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  danger: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'accent', children, style, ...props },
  ref,
) {
  const { accentSafe, accentRgb } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;

  let color: string;
  let bg: string;

  if (variant === 'accent') {
    color = accentSafe;
    bg = `rgba(${rs},0.08)`;
  } else if (variant === 'muted') {
    color = 'var(--vn-text-muted)';
    bg = 'transparent';
  } else {
    const semantic = SEMANTIC_COLORS[variant];
    color = semantic.color;
    bg = semantic.bg;
  }

  return (
    <span
      ref={ref}
      style={{
        fontFamily: 'var(--vn-font-mono)',
        fontSize: 9,
        fontWeight: 500,
        padding: '2px 6px',
        borderRadius: 3,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color,
        background: bg,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...props}
    >
      {children}
    </span>
  );
});
